import * as React from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface ComboboxOption {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  triggerClassName?: string;
  /**
   * Let the user commit a value that is not in `options` by typing it.
   * Mirrors the multi-select "Create …" affordance used for genogram health
   * conditions, so single- and multi-value entry behave the same way.
   */
  allowCustomValue?: boolean;
  /** Label for the create row. Receives the trimmed query. */
  createLabel?: (query: string) => string;
  id?: string;
  'data-testid'?: string;
  searchInputTestId?: string;
  disabled?: boolean;
}

export const Combobox: React.FC<ComboboxProps> = ({
  options,
  value,
  onValueChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyText = 'No results found.',
  className,
  triggerClassName,
  allowCustomValue = false,
  createLabel = (query) => `Create "${query}"`,
  id,
  'data-testid': testId,
  searchInputTestId,
  disabled,
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  const selectedOption = options.find((opt) => opt.value === value);
  const trimmedQuery = query.trim();
  const matchesExisting = options.some(
    (opt) =>
      opt.value.toLowerCase() === trimmedQuery.toLowerCase()
      || opt.label.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const canCreate = allowCustomValue && trimmedQuery.length > 0 && !matchesExisting;
  // Only take over filtering in creatable mode. The create row carries a
  // sentinel value (`__create__…`) that can never match the query, so leaving
  // cmdk to filter would drop it from the selectable set. Plain comboboxes
  // keep cmdk's own matcher — it scores and orders better than a substring
  // test, and changing it for existing callers is not this change's business.
  const ownFiltering = allowCustomValue;
  const visibleOptions = React.useMemo(() => {
    const filtered = options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(trimmedQuery.toLowerCase())
        || opt.value.toLowerCase().includes(trimmedQuery.toLowerCase()),
    );
    if (filtered.length === 0) {
      return [];
    } else if (!ownFiltering || trimmedQuery.length === 0) {
      return options;
    }
    return filtered;
  }, [ownFiltering, trimmedQuery, options]);
  // A custom value has no matching option, so fall back to the raw value —
  // otherwise a value the user typed would render as the placeholder.
  const triggerLabel = selectedOption?.label ?? (value || '');

  const commit = (next: string) => {
    onValueChange(next);
    setQuery('');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          data-testid={testId}
          disabled={disabled}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            !triggerLabel && 'text-muted-foreground',
            triggerClassName,
          )}
        >
          <span className="truncate">{triggerLabel || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[--radix-popover-trigger-width] p-0', className)} align="start">
        <Command shouldFilter={!ownFiltering}>
          <CommandInput
            data-testid={searchInputTestId}
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={setQuery}
          />
          <CommandList
            className="overscroll-contain"
            onWheel={(event) => event.stopPropagation()}
          >
            {/* With creation available, an empty result is not a dead end —
                the create row below carries the action, so suppress the
                "no results" message that would otherwise contradict it. */}
            {visibleOptions.length === 0 && !canCreate ? (
              <CommandItem
                forceMount
                disabled
                value="__empty__"
                className="h-auto whitespace-normal py-3 text-muted-foreground"
              >
                {emptyText}
              </CommandItem>
            ) : null}
            <CommandGroup>
              {visibleOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  data-option-value={option.value}
                  onSelect={() => commit(option.value)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
            {canCreate ? (
              <CommandGroup forceMount>
                <CommandItem
                  forceMount
                  value={`__create__${trimmedQuery}`}
                  onSelect={() => commit(trimmedQuery)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createLabel(trimmedQuery)}
                </CommandItem>
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
